/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

// src/components/ui/select.jsx
import React from "react";
import CustomSelect from "./CustomSelect"; // Import the new CustomSelect

const TRIGGER_NAME = "SelectTrigger";
const CONTENT_NAME = "SelectContent";
const ITEM_NAME    = "SelectItem";

// Helper to extract text content from React children (handles strings, numbers, arrays, fragments)
function getTextFromChildren(children) {
  if (children == null) return '';
  if (typeof children === 'string' || typeof children === 'number') return String(children);
  if (Array.isArray(children)) return children.map(getTextFromChildren).join('');
  if (React.isValidElement(children)) {
    // Handle React.Fragment or other elements
    return getTextFromChildren(children.props?.children);
  }
  return '';
}

export function Select({ value, onValueChange, children, className = "", placeholder, searchable = false, searchPlaceholder }) {
  let triggerLabelText; // This will be used as the main label for the select group
  const selectOptions = [];

  // Walk through <SelectTrigger>, <SelectContent>, and <SelectItem> children
  // to maintain compatibility with existing usage patterns if possible,
  // or to extract options if provided directly.
  React.Children.forEach(children, (child) => {
    if (child.type.displayName === TRIGGER_NAME) {
      // Assuming the child of SelectTrigger is the label text
      if (typeof child.props.children === 'string') {
        triggerLabelText = child.props.children;
      }
    }
    if (child.type.displayName === CONTENT_NAME) {
      React.Children.forEach(child.props.children, (item) => {
        if (item.type.displayName === ITEM_NAME && item.props.value !== undefined) {
          const label = getTextFromChildren(item.props.children) || String(item.props.value);
          selectOptions.push({
            value: item.props.value,
            label,
            disabled: item.props.disabled,
          });
        }
      });
    }
    // If children are directly options (e.g. from a map)
    if (child.type === 'option' && child.props.value !== undefined) {
         selectOptions.push({
            value: child.props.value,
            label: getTextFromChildren(child.props.children) || String(child.props.value),
         })
    }
  });
  
  // Determine the placeholder for CustomSelect
  // If a value is selected, CustomSelect will show its label.
  // If no value, and a placeholder prop is given to Select, use that.
  // Otherwise, if triggerLabelText was found, use that as a general placeholder.
  const customSelectPlaceholder = placeholder || triggerLabelText || "Select an option";

  return (
    <CustomSelect
      options={selectOptions}
      value={value}
      onChange={onValueChange}
      placeholder={customSelectPlaceholder}
      className={className}
  searchable={searchable}
  searchPlaceholder={searchPlaceholder}
    />
  );
}

// These are kept for compatibility if some components still use them,
// but their primary role in rendering the select is now handled by CustomSelect.
export function SelectTrigger() {
  // This component might not render anything directly visible if CustomSelect handles the button.
  // Or it could be used to pass a custom button component to CustomSelect in a more advanced setup.
  return null; // Or handle as a label provider
}
SelectTrigger.displayName = TRIGGER_NAME;

export function SelectContent() {
  // This component is conceptual for grouping SelectItem, not directly rendered.
  return null;
}
SelectContent.displayName = CONTENT_NAME;

export function SelectItem() {
  // This component is conceptual for defining options, not directly rendered.
  return null;
}
SelectItem.displayName = ITEM_NAME;
